import React from 'react';

/**
 * AuthSideVisual — reusable right-side visual for Login, Register, Forgot Password pages.
 * Phone mockup showing the admin panel screen.
 *
 * Usage: <AuthSideVisual />
 */
const AuthSideVisual = () => {
    return (
        <div className="relative mx-auto" style={{ width: 280 }}>
            {/* Phone frame */}
            <div className="relative rounded-[40px] bg-stone-900 p-2.5 shadow-2xl" style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.08) inset' }}>
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-stone-900 rounded-b-2xl z-10" />
                {/* Screen */}
                <div className="rounded-[32px] overflow-hidden bg-white" style={{ minHeight: 500 }}>
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-5 pt-8 pb-2">
                        <span className="text-[10px] font-semibold text-stone-400">18:45</span>
                        <div className="flex gap-1 items-center">
                            <div className="w-3.5 h-2 border border-stone-400 rounded-sm relative">
                                <div className="absolute inset-0.5 bg-emerald-500 rounded-[1px]" style={{ width: '70%' }} />
                            </div>
                        </div>
                    </div>

                    {/* Admin header */}
                    <div className="flex items-center justify-between px-4 pb-2.5 border-b border-stone-100">
                        <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 rounded-lg bg-rose-100 flex items-center justify-center text-[11px]">💍</div>
                            <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-[13px] font-bold text-stone-800">Admin panel</span>
                        </div>
                        <div className="flex gap-1">
                            <span className="text-[8px] font-semibold bg-stone-100 text-stone-500 py-0.5 px-1.5 rounded-md">Ana & Marko</span>
                            <span className="text-[8px] font-semibold bg-rose-500 text-white py-0.5 px-1.5 rounded-md">📄 PDF</span>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-4 border-b border-stone-100">
                        {[
                            { val: '24', label: 'Dolaze', color: 'text-emerald-600' },
                            { val: '3', label: 'Ne dolaze', color: 'text-red-500' },
                            { val: '8', label: 'Čekaju', color: 'text-stone-400' },
                            { val: '19', label: 'Ima stol', color: 'text-blue-600' },
                        ].map((s, i) => (
                            <div key={i} className={`py-2 text-center ${i < 3 ? 'border-r border-stone-100' : ''}`}>
                                <div className={`text-base font-bold ${s.color}`}>{s.val}</div>
                                <div className="text-[8px] text-stone-400 mt-0.5">{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Filter pills */}
                    <div className="px-3 py-2 border-b border-stone-100 flex gap-1 flex-wrap">
                        {[
                            { label: 'Svi (35)', active: true },
                            { label: '✓ Dolaze', active: false },
                            { label: '👥 Grupe', active: false },
                            { label: '🔒 Privatni', active: false },
                        ].map((p, i) => (
                            <span key={i} className={`text-[8px] font-semibold py-0.5 px-2 rounded-full ${p.active ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-500'}`}>{p.label}</span>
                        ))}
                    </div>

                    {/* Guest list */}
                    <div className="px-3 py-2 flex flex-col gap-1.5">
                        {[
                            { name: 'Ivan Horvat', rsvp: 'Dolazi', dotColor: 'bg-emerald-500', rsvpBg: 'bg-emerald-50', rsvpColor: 'text-emerald-600', table: 'Stol 2', group: 4 },
                            { name: 'Maja Kovač', rsvp: 'Dolazi', dotColor: 'bg-emerald-500', rsvpBg: 'bg-emerald-50', rsvpColor: 'text-emerald-600', table: 'Stol 2', group: null },
                            { name: 'Tomislav Jurić', rsvp: 'Čeka', dotColor: 'bg-stone-400', rsvpBg: 'bg-stone-50', rsvpColor: 'text-stone-400', table: '—', group: null },
                            { name: 'Petra Blažević', rsvp: 'Ne dolazi', dotColor: 'bg-red-500', rsvpBg: 'bg-red-50', rsvpColor: 'text-red-500', table: '—', group: null },
                            { name: 'Luka Matić', rsvp: 'Dolazi', dotColor: 'bg-emerald-500', rsvpBg: 'bg-emerald-50', rsvpColor: 'text-emerald-600', table: 'Stol 5', group: 3 },
                            { name: 'Ana Babić', rsvp: 'Dolazi', dotColor: 'bg-emerald-500', rsvpBg: 'bg-emerald-50', rsvpColor: 'text-emerald-600', table: 'Stol 1', group: null },
                        ].map((g, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg border border-stone-100">
                                <div className="flex items-center gap-1.5">
                                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${g.dotColor}`} />
                                    <span className="text-[10px] font-semibold text-stone-800">{g.name}</span>
                                    {g.group && <span className="text-[7px] bg-rose-100 text-rose-500 py-[1px] px-1 rounded font-semibold">👥 {g.group}</span>}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <span className={`text-[7px] font-semibold py-[2px] px-1.5 rounded ${g.rsvpBg} ${g.rsvpColor}`}>{g.rsvp}</span>
                                    <span className="text-[7px] text-stone-500 bg-stone-100 py-[2px] px-1.5 rounded">🪑 {g.table}</span>
                                </div>
                            </div>
                        ))}
                        <div className="text-center py-1">
                            <span className="text-[8px] text-stone-400">+ još 29 gostiju · Filteri, premještanje, PDF…</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthSideVisual;
